/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import {
    addNoteAddendum,
    addSessionNote,
    approveSuperviseeNote,
    archiveNote,
    changeCptCode,
    deleteDraftNote,
    editDraftNote,
    exportNotes,
    generateAiSessionNoteDraft,
    reviewSessionNoteWithAi,
    requestNoteChanges,
    recalculateUnitMetadata,
    requestSupervisorReview,
    skipSupervisorReview,
    resolveNoteChange,
    signSessionNote,
    terminateIntakeAppointmentForNote,
    updateChangeRequestOutcome,
} from "../../services/note";
import { IApiResponse } from "../../utils/types/api-response";
import {
    IAddendumDetails,
    IApproveNotePayload,
    IApproveNoteResponse,
    IChangeCode,
    IChangeCodeResponse,
    IEditDraftNote,
    IExportNotePayload,
    INewAddendum,
    INewNoteResponse,
    INewSessionNote,
    IAiSessionNoteDraft,
    IAiSessionNoteDraftRequest,
    IAiSessionNoteReview,
    IRequestChangePayload,
    ISessionNote,
    ISignNewNotePayload,
    IChangeArchiveStatus,
    IRecalculateUnitMetadataPayload,
    IRecalculateUnitMetadataResult,
    IRequestDetails,
    ITerminateIntakeAppointmentForNotePayload,
    IChangeRequestOutcomePayload,
} from "../../modules/notes/types/notes.types";

// Function to add note addendum
export function useAddNewAddendum(noteId: string) {
    return useMutation<
        IApiResponse<IAddendumDetails>,
        AxiosError<IApiResponse<IAddendumDetails>>,
        INewAddendum
    >({
        mutationKey: ["add-addendum", noteId],
        mutationFn: (data: INewAddendum) => addNoteAddendum(noteId, data),
    });
}

// Hook for adding client session note
export function useAddSessionNote() {
    return useMutation<
        IApiResponse<INewNoteResponse>,
        AxiosError<IApiResponse<INewNoteResponse>>,
        INewSessionNote
    >({
        mutationFn: (data: INewSessionNote) => addSessionNote(data),
    });
}

// Hook for signing client session note
export function useSignSessionNote() {
    return useMutation<
        IApiResponse<INewNoteResponse>,
        AxiosError<IApiResponse<INewNoteResponse>>,
        { noteId: string; data: ISignNewNotePayload }
    >({
        mutationKey: ["sign-note"],
        mutationFn: (payload: { noteId: string; data: ISignNewNotePayload }) =>
            signSessionNote(payload),
    });
}

export function useDeleteDraftNote() {
    interface IDeleteNotesResponse {
        deleted_notes: string[];
        undeleted_notes: string[];
    }
    return useMutation<
        IApiResponse<IDeleteNotesResponse>,
        AxiosError<IApiResponse<IDeleteNotesResponse>>,
        string[]
    >({
        mutationKey: ["delete-draft-notes"],
        mutationFn: (noteIds: string[]) => deleteDraftNote(noteIds),
    });
}

export function useApproveNote() {
    return useMutation<
        IApiResponse<IApproveNoteResponse>,
        AxiosError<IApiResponse<IApproveNoteResponse>>,
        IApproveNotePayload
    >({
        mutationKey: ["approve-note"],
        mutationFn: (data: IApproveNotePayload) => approveSuperviseeNote(data),
    });
}

export function useRequestChange(noteId: string) {
    return useMutation<
        IApiResponse<IRequestDetails>,
        AxiosError<IApiResponse<IRequestDetails>>,
        IRequestChangePayload
    >({
        mutationKey: ["request-change", noteId],
        mutationFn: (data: IRequestChangePayload) =>
            requestNoteChanges(noteId, data),
    });
}

export function useEditDraftNote() {
    return useMutation<
        IApiResponse<ISessionNote>,
        AxiosError<IApiResponse<ISessionNote>>,
        {
            noteId: string;
            data: IEditDraftNote;
        }
    >({
        mutationKey: ["edit-note"],
        mutationFn: (payload: { noteId: string; data: IEditDraftNote }) =>
            editDraftNote(payload),
    });
}

// Hook for getting exported notes
export function useExportNotes() {
    interface IExportResponseData {
        export_job_id: string;
        expected_pdf_count: number;
    }
    return useMutation<
        IApiResponse<IExportResponseData>,
        AxiosError<IApiResponse<IExportResponseData>>,
        IExportNotePayload
    >({
        mutationFn: (data: IExportNotePayload) => exportNotes(data),
    });
}

// Hook for changing cpt code
interface IChangeCptCodeResponse extends IApiResponse<IChangeCodeResponse[]> {
    claim_requires_attention?: boolean;
}

export function useChangeCptCode() {
    const queryClient = useQueryClient();

    return useMutation<
        IChangeCptCodeResponse,
        AxiosError<IChangeCptCodeResponse>,
        IChangeCode
    >({
        mutationKey: ["change-code"],
        mutationFn: (data: IChangeCode) => changeCptCode(data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["claims"] });
            queryClient.invalidateQueries({ queryKey: ["claims-stats"] });
        },
    });
}

export function useRecalculateUnitMetadata() {
    return useMutation<
        IApiResponse<IRecalculateUnitMetadataResult[]>,
        AxiosError<IApiResponse<IRecalculateUnitMetadataResult[]>>,
        IRecalculateUnitMetadataPayload
    >({
        mutationKey: ["recalculate-unit-metadata"],
        mutationFn: (data: IRecalculateUnitMetadataPayload) =>
            recalculateUnitMetadata(data),
    });
}

export function useGenerateAiSessionNoteDraft(noteId: string) {
    return useMutation<
        IApiResponse<IAiSessionNoteDraft>,
        AxiosError<IApiResponse<IAiSessionNoteDraft>>,
        IAiSessionNoteDraftRequest
    >({
        mutationKey: ["ai-session-note-draft", noteId],
        mutationFn: (data) => generateAiSessionNoteDraft(noteId, data),
    });
}

export function useReviewSessionNoteWithAi(noteId: string) {
    const queryClient = useQueryClient();
    return useMutation<
        IApiResponse<IAiSessionNoteReview>,
        AxiosError<IApiResponse<IAiSessionNoteReview>>,
        void
    >({
        mutationKey: ["ai-session-note-review", noteId],
        mutationFn: () => reviewSessionNoteWithAi(noteId),
        onError: (error) => {
            if (error.response?.status === 403) {
                queryClient.invalidateQueries({
                    queryKey: ["user-details"],
                });
            }
        },
    });
}

export function useChangeNoteArchiveStatus() {
    interface IChangeArchiveStatusResponse {
        archived_notes: string[];
        unarchived_notes: string[];
    }

    return useMutation<
        IApiResponse<IChangeArchiveStatusResponse>,
        AxiosError<IApiResponse<IChangeArchiveStatusResponse>>,
        IChangeArchiveStatus
    >({
        mutationKey: ["archive-note"],
        mutationFn: (data: IChangeArchiveStatus) => archiveNote(data),
    });
}

export function useResolveNoteChange(noteId: string) {
    return useMutation<
        IApiResponse<{ message: string }>,
        AxiosError<IApiResponse<{ message: string }>>,
        IChangeRequestOutcomePayload
    >({
        mutationKey: ["resolve-change"],
        mutationFn: (data: IChangeRequestOutcomePayload) =>
            resolveNoteChange(noteId, data),
    });
}

export function useUpdateChangeRequestOutcome(
    noteId: string,
    requestId: string
) {
    return useMutation<
        IApiResponse<{ message: string }>,
        AxiosError<IApiResponse<{ message: string }>>,
        IChangeRequestOutcomePayload
    >({
        mutationKey: ["update-change-request-outcome", requestId],
        mutationFn: (data: IChangeRequestOutcomePayload) =>
            updateChangeRequestOutcome(noteId, requestId, data),
    });
}

export function useRequestSupervisorReview() {
    return useMutation<
        IApiResponse<{ message: string }>,
        AxiosError<IApiResponse<{ message: string }>>,
        string
    >({
        mutationKey: ["request-supervisor-review"],
        mutationFn: (noteId: string) => requestSupervisorReview(noteId),
    });
}

export function useSkipSupervisorReview() {
    interface ISkipSupervisorReviewResponse {
        skipped_notes: string[];
        unskipped_notes: string[];
    }

    return useMutation<
        IApiResponse<ISkipSupervisorReviewResponse>,
        AxiosError<IApiResponse<ISkipSupervisorReviewResponse>>,
        string[]
    >({
        mutationKey: ["skip-supervisor-review"],
        mutationFn: (noteIds: string[]) => skipSupervisorReview(noteIds),
    });
}

export function useTerminateIntakeAppointmentForNote() {
    return useMutation<
        IApiResponse<null>,
        AxiosError<IApiResponse<null>>,
        ITerminateIntakeAppointmentForNotePayload
    >({
        mutationKey: ["terminate-intake-appointment-note"],
        mutationFn: ({ intakeClientId, externalSessionId, payload }) =>
            terminateIntakeAppointmentForNote(
                intakeClientId,
                externalSessionId,
                payload
            ),
    });
}
